
import React from 'react';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

export default function IndustriesScale() {
  const { t } = useTranslation();
  const ref = useRef(null);
  const inView = useInView(ref, { once: false, amount: 0.2 });

  // Industries with their styling
  const industries = [
    {
      title: t('industries.gym.title'),
      description: t('industries.gym.description'),
      glowColor: '#0077BE',
      accentColor: '#4A90E2',
    },
    {
      title: t('industries.realestate.title'),
      description: t('industries.realestate.description'),
      glowColor: '#8B7355',
      accentColor: '#D4A574',
    },
    {
      title: t('industries.services.title'),
      description: t('industries.services.description'),
      glowColor: '#0099FF',
      accentColor: '#FFA500',
    },
  ].map(ind => ({
    title: ind.title,
    glowColor: ind.glowColor,
    accentColor: ind.accentColor,
    text: ind.description,
  }));

  return (
    <section
      ref={ref}
      style={{
        background: '#F5F5F5',
        padding: '8rem 2rem',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Subtle Background Gradient */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(135deg, rgba(0, 119, 190, 0.02) 0%, rgba(255, 149, 0, 0.01) 100%)',
          zIndex: 0,
        }}
      />

      {/* Content */}
      <div
        style={{
          maxWidth: '1400px',
          margin: '0 auto',
          position: 'relative',
          zIndex: 10,
        }}
      >
        {/* Section Header */}
        <div
          style={{
            textAlign: 'center',
            marginBottom: '5rem',
            maxWidth: '900px',
            margin: '0 auto 5rem',
          }}
        >
          <h2
            style={{
              fontSize: 'clamp(2.8rem, 5vw, 3.8rem)',
              fontWeight: 800,
              color: '#333333',
              fontFamily: 'Outfit, sans-serif',
              marginBottom: '1.2rem',
              letterSpacing: '-0.02em',
              lineHeight: 1.1,
            }}
          >
            {t('industries.title')}
          </h2>
          <p
            style={{
              fontSize: '1.15rem',
              color: '#666666',
              fontFamily: 'Outfit, sans-serif',
              lineHeight: 1.7,
              fontWeight: 400,
              letterSpacing: '-0.005em',
            }}
          >
            {t('industries.subtitle')}
          </p>
        </div>

        {/* Industries Grid */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '3rem',
            maxWidth: '1200px',
            margin: '0 auto 5rem',
          }}
        >
          {industries.map((industry, index) => (
            <IndustryCard key={index} industry={industry} index={index} inView={inView} />
          ))}
        </div>

        {/* CTA Section */}
        <div
          style={{
            textAlign: 'center',
            display: 'flex',
            gap: '1.5rem',
            justifyContent: 'center',
            flexWrap: 'wrap',
            padding: '4rem 2rem',
            background: 'rgba(0, 119, 190, 0.08)',
            borderRadius: '20px',
            border: '1px solid rgba(0, 119, 190, 0.2)',
          }}
        >
          {/* Secondary CTA - Show All Pillars */}
          <a
            href="#industries"
            style={{
              color: '#0077BE',
              padding: '1.1rem 2.5rem',
              borderRadius: '8px',
              textDecoration: 'none',
              fontWeight: 700,
              fontSize: '0.95rem',
              fontFamily: 'Outfit, sans-serif',
              letterSpacing: '-0.01em',
              border: '2px solid rgba(0, 119, 190, 0.5)',
              background: 'transparent',
              cursor: 'pointer',
              display: 'inline-block',
            }}
          >
            {t('header.services')} →
          </a>

          {/* Primary CTA - Claim Your Audit */}
          <a
            href="#audit"
            style={{
              background: '#FF9500',
              color: '#FFFFFF',
              padding: '1.1rem 2.5rem',
              borderRadius: '8px',
              textDecoration: 'none',
              fontWeight: 700,
              fontSize: '0.95rem',
              fontFamily: 'Outfit, sans-serif',
              letterSpacing: '-0.01em',
              border: 'none',
              cursor: 'pointer',
              boxShadow: '0 6px 16px rgba(0, 119, 190, 0.3)',
              display: 'inline-block',
            }}
          >
            {t('auditForm.button')}
          </a>
        </div>
      </div>
    </section>
  );
}

function IndustryCard({ industry, index, inView }) {
  return (
    <div
      style={{
        background: '#FFFFFF',
        border: '1px solid #E0E0E0',
        borderRadius: '20px',
        padding: '3.5rem 2.5rem',
        position: 'relative',
        overflow: 'hidden',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
        textAlign: 'center',
      }}
    >
      {/* Subtle Gradient Background */}
      <div
        style={{
          position: 'absolute',
          top: -100,
          right: -100,
          width: '300px',
          height: '300px',
          background: `radial-gradient(circle, rgba(0, 119, 190, 0.08) 0%, transparent 70%)`,
          borderRadius: '50%',
          filter: 'blur(40px)',
          pointerEvents: 'none',
        }}
      />

      {/* Inner Subtle Glow */}
      <div
        style={{
          position: 'absolute',
          top: '20%',
          right: '10%',
          width: '150px',
          height: '150px',
          background: `radial-gradient(circle, rgba(0, 119, 190, 0.05) 0%, transparent 70%)`,
          borderRadius: '50%',
          filter: 'blur(20px)',
          pointerEvents: 'none',
        }}
      />

      {/* Top Orange Accent Line */}
      <div
        style={{
          height: '3px',
          width: '40px',
          background: '#FF9500',
          borderRadius: '2px',
          marginBottom: '2rem',
          marginLeft: 'auto',
          marginRight: 'auto',
          position: 'relative',
          zIndex: 2,
        }}
      />

      {/* Title */}
      <h3
        style={{
          fontSize: '1.8rem',
          fontWeight: 800,
          color: '#0077BE',
          marginBottom: '1.5rem',
          fontFamily: 'Outfit, sans-serif',
          letterSpacing: '-0.01em',
          position: 'relative',
          zIndex: 2,
        }}
      >
        {industry.title}
      </h3>

      {/* Description */}
      <p
        style={{
          fontSize: '1.05rem',
          lineHeight: 1.8,
          color: '#555555',
          fontFamily: 'Outfit, sans-serif',
          fontWeight: 400,
          marginBottom: '2rem',
          position: 'relative',
          zIndex: 2,
        }}
      >
        {industry.text}
      </p>

    </div>
  );
}
